/**
 * Chat pill "ให้ AI สกัดข้อมูลจอง" → right-rail plumbing, shared by `CustomerPanelV2026` and
 * `ProfessionalPanelV2026`.
 *
 * Host panel must already expose:
 *   activeTab              // Number — written to 4 on a press
 *   selectedConversation   // prop — its id change is what clears the queue
 *   admin                  // mapState('admin', ['admin'])
 *   toast: { open, variant, title, message, icon }   // + a <BaseToast> bound to it
 *
 * The toasts live here, not in `AiFunctionCallingV2026`: that pane is `v-if` and would already be
 * destroyed when an outcome lands after a tab switch.
 */

import { mapState } from 'vuex'

// One-shot per press / per notice: a collapse-expand remount must not re-jump the tab or re-fire
// a toast.
let appliedFcToken = null
let noticedFcToken = null

// Fixed copy by fcNotice.kind; success and unreadable are built from the payload in `applyFcNotice`.
const FC_TOASTS = {
  denied: {
    variant: 'danger',
    icon: 'alert',
    title: 'วิเคราะห์ข้อมูลไม่ได้',
    message: 'ลูกค้ายังไม่ได้ลงทะเบียนเป็นสมาชิก'
  },
  empty: {
    // success variant + alert glyph: a neutral notice, not a failure — the request worked.
    variant: 'success',
    icon: 'alert',
    title: 'ไม่พบข้อมูลการจองในข้อความนี้',
    message: 'ลองเพิ่มข้อความที่มีวันที่ เวลา หรือที่อยู่ แล้ววิเคราะห์อีกครั้ง'
  },
  server: {
    variant: 'danger',
    icon: 'alert',
    title: 'วิเคราะห์ข้อความไม่สำเร็จ',
    message: 'ตรวจสอบการเชื่อมต่อแล้วกด "ลองใหม่"'
  },
  network: {
    variant: 'danger',
    icon: 'alert',
    title: 'วิเคราะห์ข้อความไม่สำเร็จ',
    message: 'ตรวจสอบการเชื่อมต่อแล้วกด "ลองใหม่"'
  },
  cleared: {
    variant: 'success',
    icon: 'check',
    title: 'ล้างคิวข้อความแล้ว',
    message: ''
  },
  opened: {
    variant: 'success',
    icon: 'check',
    title: 'เปิดหน้าสร้างรายการจองในแท็บใหม่',
    message: ''
  },
  popup_blocked: {
    variant: 'danger',
    icon: 'alert',
    title: 'เบราว์เซอร์บล็อกแท็บใหม่',
    message: 'อนุญาต pop-up ของเว็บนี้ แล้วกดสร้างรายการจองอีกครั้ง'
  }
}

const SUCCESS_MESSAGES = {
  booking: 'พบข้อมูลการจอง',
  tax: 'พบข้อมูลใบกำกับภาษี'
}

export default {
  data () {
    return {
      fcConversationId: null
    }
  },
  computed: {
    // `departmentIds` is also mapped by ProfessionalPanelV2026; its own entry wins the merge and
    // carries the identical value.
    ...mapState('aiAssistant', ['fcRequest', 'fcNotice', 'multiMessages', 'departmentIds']),
    // Same predicate the chat pill itself uses — CustomerPanelV2026's tab 4 is otherwise ungated.
    canSeeFunctionCalling () {
      return !!(this.admin && Array.isArray(this.departmentIds) &&
        this.departmentIds.includes(this.admin.department_id))
    }
  },
  watch: {
    fcRequest () {
      this.applyFcRequest()
    },
    fcNotice () {
      this.applyFcNotice()
    },
    selectedConversation () {
      this.syncFcConversation()
    }
  },
  // A request written before this panel mounted (collapsed rail) never fires the watcher.
  mounted () {
    // Seed only — clearing here would eat the very press that mounted the panel.
    this.fcConversationId = this.selectedConversation ? this.selectedConversation.id : null
    this.applyFcRequest()
  },
  methods: {
    applyFcRequest () {
      const request = this.fcRequest
      if (!request || !this.canSeeFunctionCalling || request.token === appliedFcToken) return
      appliedFcToken = request.token
      this.activeTab = 4
      const count = Array.isArray(this.multiMessages) ? this.multiMessages.length : 0
      this.fcToast('success', 'check', 'เพิ่มข้อความให้ AI แล้ว',
        `อยู่ในคิว ${count} ข้อความ · กด "ช่วยฉันวิเคราะห์" เพื่อเริ่ม`)
    },
    applyFcNotice () {
      const notice = this.fcNotice
      if (!notice || !this.canSeeFunctionCalling || notice.token === noticedFcToken) return
      noticedFcToken = notice.token
      if (notice.kind === 'success') {
        this.fcToast('success', 'check', 'AI สกัดข้อมูลเสร็จแล้ว', this.fcSuccessMessage(notice))
        return
      }
      if (notice.kind === 'unreadable') {
        this.fcToast('success', 'alert', 'AI อ่านข้อมูลจากข้อความนี้ไม่ได้',
          `ประเภทที่ระบบตอบกลับ: ${notice.functionName || 'ไม่ระบุ'}`)
        return
      }
      const preset = FC_TOASTS[notice.kind]
      if (!preset) return
      this.fcToast(preset.variant, preset.icon, preset.title, preset.message)
    },
    fcSuccessMessage (notice) {
      if (notice.shape === 'order') {
        return notice.orderCode ? `พบเลขที่การจอง #${notice.orderCode}` : 'พบเลขที่การจอง'
      }
      return SUCCESS_MESSAGES[notice.shape] || 'พบข้อมูลจากข้อความ'
    },
    // BaseToast only re-arms its auto-hide timer on the `open` transition, so a toast fired while
    // one is still open would inherit the remaining time. Close, then re-open on the next tick.
    fcToast (variant, icon, title, message) {
      this.toast = Object.assign({}, this.toast, { open: false })
      this.$nextTick(() => {
        this.toast = { open: true, variant, title, message, icon }
      })
    },
    // The store is persisted, so without this an admin meets yesterday's chips and result.
    syncFcConversation () {
      const id = this.selectedConversation ? this.selectedConversation.id : null
      if (id === this.fcConversationId) return
      this.fcConversationId = id
      this.$store.commit('aiAssistant/CLEAR_FC')
      this.$store.commit('aiAssistant/CLEAR_MULTI_MESSAGES')
    }
  }
}
